import { Calendar, Package, Search, Trash2, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DISPOSAL_STATUS, FLEXI_TANKS } from './mockAssetData';

function DisposalBadge({ status }) {
  const config = DISPOSAL_STATUS[status];
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

export default function FlexiTanksGrid({ tanks = FLEXI_TANKS }) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return tanks.filter(
      (t) =>
        t.id.toLowerCase().includes(q) ||
        t.product.toLowerCase().includes(q) ||
        t.customer.toLowerCase().includes(q) ||
        DISPOSAL_STATUS[t.disposal].label.toLowerCase().includes(q),
    );
  }, [tanks, query]);

  const pendingCount = tanks.filter((t) => t.disposal === 'pending_disposal').length;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Flexi Tank (Single-Use) Tracker</h3>
            <p className="text-[11px] text-logi-text0">
              Disposable bag-in-container assets · Customer allocation · Disposal lifecycle
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-700 ring-1 ring-amber-500/25">
              {pendingCount} pending disposal
            </span>
          )}
          <div className="relative w-full sm:w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-logi-text0" />
            <input
              type="search"
              placeholder="Search flexi tanks…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-lg border border-logi-grid bg-white py-1.5 pl-8 pr-3 text-xs text-logi-text placeholder-logi-subtle outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-logi-accent/30"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
        {filtered.map((tank) => (
          <article
            key={tank.id}
            className={`rounded-lg border bg-white p-2.5 backdrop-blur-sm transition-colors hover:bg-logi-silver/80 ${
              tank.disposal === 'pending_disposal'
                ? 'border-amber-200'
                : tank.disposal === 'disposed'
                  ? 'border-logi-grid/30 opacity-75'
                  : 'border-logi-grid'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-mono text-xs font-bold text-logi-accent">{tank.id}</span>
              {tank.disposal === 'pending_disposal' && (
                <Trash2 className="h-3.5 w-3.5 text-amber-700" strokeWidth={1.75} />
              )}
            </div>

            <p className="mt-1 truncate text-[11px] font-medium text-logi-text">{tank.product}</p>

            <div className="mt-2 space-y-1.5">
              <div className="flex items-center gap-1.5 text-[10px] text-logi-muted">
                <Calendar className="h-3 w-3 flex-shrink-0 text-logi-text0" />
                <span>
                  Installed{' '}
                  <span className="font-mono text-logi-muted">{tank.installDate}</span>
                </span>
              </div>
              <div className="flex items-start gap-1.5 text-[10px] text-logi-muted">
                <User className="mt-0.5 h-3 w-3 flex-shrink-0 text-logi-text0" />
                <span className="line-clamp-2 leading-tight">{tank.customer}</span>
              </div>
            </div>

            <div className="mt-2 border-t border-logi-grid/80 pt-2">
              <DisposalBadge status={tank.disposal} />
            </div>
          </article>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="py-8 text-center text-sm text-logi-text0">No flexi tanks match your search.</p>
      )}
    </section>
  );
}
